import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '../../data/http';
import { getInsightsOverview, listAuditLog, listTemplateUsage } from '../../data/insights';
import { useDuration } from '../../hooks/useDuration';
import type { AuditCategory, AuditEntrySummary, InsightsOverview, TemplateUsage } from '../../data/types';

const CATEGORIES: AuditCategory[] = ['Template', 'Reference', 'Service', 'User'];

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{value}</dd>
    </div>
  );
}

export function InsightsPage() {
  const { t, i18n } = useTranslation();
  const { fromMinutes } = useDuration();

  const [overview, setOverview] = useState<InsightsOverview | null>(null);
  const [usage, setUsage] = useState<TemplateUsage[]>([]);
  const [entries, setEntries] = useState<AuditEntrySummary[]>([]);
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (filter: AuditCategory | '') =>
      Promise.all([getInsightsOverview(), listTemplateUsage(), listAuditLog(filter || undefined)])
        .then(([summary, templates, audit]) => {
          setOverview(summary);
          setUsage(templates);
          setEntries(audit);
          setError(null);
        })
        .catch((err: unknown) => setError(describeApiError(err, t('errors.loadInsights'))))
        .finally(() => setLoading(false)),
    [t]
  );

  useEffect(() => {
    void load(category);
  }, [load, category]);

  // Signed: templates people take longer on than the estimate subtract from the total.
  const duration = (minutes: number) =>
    minutes < 0 ? `-${fromMinutes(Math.abs(minutes))}` : fromMinutes(minutes);

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

  // No defaultValue fallback: a missing key must show as the key, not pass silently.
  const actionLabel = (action: string) => t(`settings.insights.actions.${action}`);

  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('settings.insights.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.insights.subtitle')}</p>
      </div>

      {overview && (
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label={t('settings.insights.templates')} value={String(overview.templates)} />
          <Stat label={t('settings.insights.published')} value={String(overview.publishedTemplates)} />
          <Stat label={t('settings.insights.documentsGenerated')} value={String(overview.documentsGenerated)} />
          <Stat label={t('settings.insights.timeSaved')} value={duration(overview.timeSavedMinutes)} />
          <Stat label={t('settings.insights.referenceEntries')} value={String(overview.referenceEntries)} />
          <Stat label={t('settings.insights.services')} value={String(overview.services)} />
        </dl>
      )}

      <section>
        <h2 className="text-lg font-semibold text-foreground">{t('settings.insights.usageTitle')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.insights.usageSubtitle')}</p>

        {usage.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">{t('settings.insights.noUsage')}</p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">{t('settings.insights.columnTemplate')}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">{t('settings.insights.columnUsage')}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">{t('settings.insights.columnMeasured')}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">{t('settings.insights.columnEstimate')}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">{t('settings.insights.columnSaved')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {usage.map((row) => (
                  <tr key={row.templateId}>
                    <td className="px-4 py-2 text-foreground">
                      {row.name}
                      {!row.isPublished && (
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {t('settings.insights.draft')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{row.usageCount}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{row.measuredDocuments}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {fromMinutes(row.estimatedMinutes)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{duration(row.timeSavedMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('settings.insights.auditTitle')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('settings.insights.auditSubtitle')}</p>
          </div>
          <div>
            <label htmlFor="audit-category" className="sr-only">
              {t('settings.insights.allCategories')}
            </label>
            <select
              id="audit-category"
              value={category}
              onChange={(event) => setCategory(event.target.value as AuditCategory | '')}
              className="rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
            >
              <option value="">{t('settings.insights.allCategories')}</option>
              {CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {t(`settings.insights.categories.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">{t('settings.insights.noAudit')}</p>
        ) : (
          <ul className="mt-4 divide-y divide-border rounded-md border border-border">
            {entries.map((entry) => (
              <li key={`${entry.actionAtUtc}-${entry.targetId}-${entry.action}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3">
                <span className="text-sm font-medium text-foreground">{actionLabel(entry.action)}</span>
                <span className="text-sm text-foreground">{entry.targetName || entry.targetId}</span>
                {entry.details && <span className="text-xs text-muted-foreground">{entry.details}</span>}
                <span className="ml-auto text-xs text-muted-foreground">
                  {entry.actorId} — {formatDate(entry.actionAtUtc)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
